import { useEffect, useState } from "react";
import { useApp } from "../../context/AppContext";
import {
  favoriteCousinConfigFor,
  rememberedConfigFor,
  weddingConfigFor,
} from "../../family/celebrations";
import {
  ageDescription,
  expectingLine,
  memoryLine,
  residenceLine,
} from "../../utils/personDisplay";
import { relationPath } from "../../family/relationPath";
import { ModalSheet } from "../shared/ModalSheet";
import { Avatar } from "../shared/Avatar";
import { EncryptedPhoto } from "../shared/EncryptedPhoto";

function WeddingCelebrationModal({
  personId,
  onClose,
}: {
  personId: string;
  onClose: () => void;
}) {
  const config = weddingConfigFor(personId);
  const { person } = useApp();
  if (!config) return null;
  const p = person(personId);
  return (
    <div className="celebration-full" role="dialog" aria-label={config.celebrationBadge}>
      <button type="button" className="celebration-close" onClick={onClose}>
        ✕
      </button>
      <div className="celebration-badge">{config.celebrationBadge}</div>
      {p ? <Avatar person={p} size={96} ring /> : null}
      <h2 className="title-serif" style={{ color: "white", margin: "8px 0" }}>
        {config.headline}
      </h2>
      <p className="celebration-sub">{config.subheadline}</p>
      <div className="celebration-badges">
        {config.badges.map((b) => (
          <span key={b}>{b}</span>
        ))}
      </div>
      <div className="celebration-photos">
        {config.photoStems.map((stem) => (
          <EncryptedPhoto
            key={stem}
            stem={stem}
            alt={config.displayNames}
            className="celebration-photo"
            fit="cover"
          />
        ))}
      </div>
      <p className="celebration-tagline">{config.panelTagline}</p>
      <button type="button" className="btn-primary" onClick={onClose}>
        {config.firstPlayCTA}
      </button>
    </div>
  );
}

function FavoriteCousinModal({
  personId,
  onClose,
}: {
  personId: string;
  onClose: () => void;
}) {
  const config = favoriteCousinConfigFor(personId);
  const { person } = useApp();
  if (!config) return null;
  const p = person(personId);
  return (
    <div className="celebration-full celebration-gold" role="dialog">
      <button type="button" className="celebration-close" onClick={onClose}>
        ✕
      </button>
      <div className="celebration-badge">👑 FAVORITE COUSIN</div>
      {p ? <Avatar person={p} size={96} ring /> : null}
      <h2 className="title-serif" style={{ color: "white", margin: "8px 0" }}>
        {config.headline}
      </h2>
      <p className="celebration-sub">{config.subheadline}</p>
      <div className="celebration-badges">
        {config.badges.map((b) => (
          <span key={b}>{b}</span>
        ))}
      </div>
      {config.showsIconicPanel && config.panelTagline ? (
        <p className="celebration-tagline">{config.panelTagline}</p>
      ) : null}
      <button type="button" className="btn-primary" onClick={onClose}>
        Okay, {config.displayName === "Victoria" || config.displayName === "Kyle"
          ? config.displayName === "Victoria"
            ? "she's"
            : "he's"
          : "they're"}{" "}
        the best 🙌
      </button>
    </div>
  );
}

function RememberedModal({
  personId,
  onClose,
}: {
  personId: string;
  onClose: () => void;
}) {
  const config = rememberedConfigFor(personId);
  const { person } = useApp();
  if (!config) return null;
  const p = person(personId);
  return (
    <div className="celebration-full celebration-memorial" role="dialog">
      <button type="button" className="celebration-close" onClick={onClose}>
        ✕
      </button>
      {p ? <Avatar person={p} size={96} ring /> : null}
      <p className="muted" style={{ color: "rgba(255,255,255,0.7)" }}>
        {config.years}
      </p>
      <h2 className="title-serif" style={{ color: "white", margin: "8px 0" }}>
        {config.headline}
      </h2>
      <p className="celebration-sub">{config.subheadline}</p>
      <p className="celebration-tagline">{config.tribute}</p>
      <p className="celebration-sub" style={{ whiteSpace: "pre-line" }}>
        {config.semicolonCaption}
      </p>
      <button type="button" className="btn-primary" onClick={onClose}>
        {config.closingButton}
      </button>
    </div>
  );
}

export function PersonDetailSheet() {
  const {
    selectedPersonId,
    setSelectedPersonId,
    person,
    newsFeed,
    me,
    setShowSettings,
    bundle,
    pushPerson,
    popPerson,
    personStack,
  } = useApp();
  const [showWedding, setShowWedding] = useState(false);
  const [showFavorite, setShowFavorite] = useState(false);
  const [showRemembered, setShowRemembered] = useState(false);

  useEffect(() => {
    setShowWedding(false);
    setShowFavorite(false);
    setShowRemembered(false);
    if (!selectedPersonId) return;
    if (rememberedConfigFor(selectedPersonId)) {
      setShowRemembered(true);
      return;
    }
    if (weddingConfigFor(selectedPersonId)) {
      setShowWedding(true);
      return;
    }
    if (favoriteCousinConfigFor(selectedPersonId)) {
      setShowFavorite(true);
    }
  }, [selectedPersonId]);

  if (!selectedPersonId) return null;
  const p = person(selectedPersonId);
  if (!p) return null;

  const talkingPoint = newsFeed?.talking_points.find((t) => t.person_id === p.person_id);
  const override = newsFeed?.location_overrides.find((o) => o.person_id === p.person_id);
  const partners = p.spouse_ids.map((id) => person(id)).filter(Boolean);
  const children = p.child_ids.map((id) => person(id)).filter(Boolean);
  const isMe = me?.person_id === p.person_id;
  const wedding = weddingConfigFor(p.person_id);
  const favorite = favoriteCousinConfigFor(p.person_id);
  const remembered = rememberedConfigFor(p.person_id);
  const isGrandparent =
    p.person_id === "robert-edwin-sundius" ||
    p.person_id === "catherine-margaret-thompson" ||
    p.display_name === "Pop Pop" ||
    p.display_name === "Grammie";
  const gallery = bundle?.grandparent_gallery ?? [];

  if (showWedding) {
    return <WeddingCelebrationModal personId={p.person_id} onClose={() => setShowWedding(false)} />;
  }
  if (showFavorite) {
    return <FavoriteCousinModal personId={p.person_id} onClose={() => setShowFavorite(false)} />;
  }
  if (showRemembered) {
    return <RememberedModal personId={p.person_id} onClose={() => setShowRemembered(false)} />;
  }

  const path = bundle ? relationPath(bundle, p.person_id) : [];
  const ageLine = ageDescription(p);
  const memory = memoryLine(p);
  const expecting = expectingLine(p);
  const residence = residenceLine(p, override ?? undefined);

  return (
    <ModalSheet
      open
      onClose={() => setSelectedPersonId(null)}
      className="person-detail-sheet"
    >
      <div className="person-detail-toolbar">
        {personStack.length > 1 ? (
          <button type="button" className="btn-ghost person-detail-back" onClick={() => popPerson()}>
            ← Back
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          className="sheet-close"
          aria-label="Close"
          onClick={() => setSelectedPersonId(null)}
        >
          ✕
        </button>
      </div>
      <div className="sheet-body stack person-detail-body">
        <div className="person-detail-hero">
          <Avatar person={p} size={96} ring />
          <div>
            <h2 className="title-serif person-detail-name">
              {p.display_name}
            </h2>
            {p.legal_name && p.legal_name !== p.display_name ? (
              <p className="legal-name">{p.legal_name}</p>
            ) : null}
            {p.relation_label ? (
              <p className="person-detail-relation">{p.relation_label}</p>
            ) : null}
            {memory ? <p className="muted">{memory}</p> : null}
            {expecting ? <p className="gold-text">{expecting}</p> : null}
            {!memory && ageLine ? <p className="muted">{ageLine}</p> : null}
            {residence ? <p className="muted">{residence}</p> : null}
          </div>
        </div>
        {p.adjectives && p.adjectives.length > 0 ? (
          <div className="adjective-row" role="list">
            {p.adjectives.map((adj) => (
              <span key={adj} className="adjective-chip" role="listitem">
                {adj}
              </span>
            ))}
          </div>
        ) : null}
        {talkingPoint ? (
          <div className="card pad-screen">
            <strong>Talking point</strong>
            <p>{talkingPoint.text}</p>
          </div>
        ) : null}
        {path.length > 1 && !p.is_partner ? (
          <div>
            <h3 className="section-title">How you&apos;re connected</h3>
            <div className="relation-path">
              {path.map((node, i) => (
                <span key={node.person_id}>
                  {i > 0 ? " → " : ""}
                  <button type="button" className="linkish" onClick={() => pushPerson(node.person_id)}>
                    {node.display_name}
                  </button>
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {wedding ? (
          <button type="button" className="btn-primary" onClick={() => setShowWedding(true)}>
            {wedding.celebrationBadge}
          </button>
        ) : null}
        {favorite ? (
          <button type="button" className="btn-primary" onClick={() => setShowFavorite(true)}>
            Favorite cousin celebration
          </button>
        ) : null}
        {remembered ? (
          <button type="button" className="btn-primary" onClick={() => setShowRemembered(true)}>
            Remembered with love
          </button>
        ) : null}

        {isGrandparent && gallery.length > 0 ? (
          <div>
            <h3 className="section-title">Family photos</h3>
            <div className="heritage-gallery-grid">
              {gallery.map((photo) => (
                <figure key={photo.stem} className="heritage-gallery-card">
                  <EncryptedPhoto
                    stem={photo.stem}
                    alt={photo.label}
                    className="heritage-gallery-photo"
                    fit="cover"
                  />
                  <figcaption>{photo.label}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        ) : null}

        {partners.length > 0 ? (
          <div>
            <h3 className="section-title">{bondSectionTitle(p.relation_label)}</h3>
            <div className="grid-faces">
              {partners.map((partner) =>
                partner ? (
                  <button
                    key={partner.person_id}
                    type="button"
                    className="face-card"
                    onClick={() => pushPerson(partner.person_id)}
                  >
                    <Avatar person={partner} size={64} />
                    <div className="name">{partner.display_name}</div>
                  </button>
                ) : null,
              )}
            </div>
          </div>
        ) : null}
        {children.length > 0 ? (
          <div>
            <h3 className="section-title">Kids</h3>
            <div className="grid-faces">
              {children.map((child) =>
                child ? (
                  <button
                    key={child.person_id}
                    type="button"
                    className="face-card"
                    onClick={() => pushPerson(child.person_id)}
                  >
                    <Avatar person={child} size={64} />
                    <div className="name">{child.display_name}</div>
                  </button>
                ) : null,
              )}
            </div>
          </div>
        ) : null}
        {isMe ? (
          <button type="button" className="btn-primary" onClick={() => setShowSettings(true)}>
            Edit my profile
          </button>
        ) : null}
      </div>
    </ModalSheet>
  );
}

export function bondSectionTitle(relationLabel?: string | null): string {
  if (!relationLabel) return "Partner";
  const lower = relationLabel.toLowerCase();
  if (lower.includes("engaged") || lower.includes("fiancé")) return "Fiancé";
  if (lower.includes("married")) return "Spouse";
  if (lower.includes("partner")) return "Partner";
  return "Partner";
}
